import React, { useEffect, useMemo, useRef, useState } from "react";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { MRT_ColumnDef, MRT_ColumnOrderState, useMaterialReactTable } from "material-react-table";

import {
  useAccountSelector,
  useNumberFormatSelector,
  useReportsActions,
  useReportsQueriesSelector,
  useReportsTableSelector,
  useUserActions
} from "../../../store";
import { ClientDealTableRow, ReportTableColumn } from "../../../store/types";
import { SelectOption } from "../../forms";
import { MaterialTooltip } from "../../tooltips/MaterialTooltip";
import { PricingCell } from "../Measures-table/PricingCell";
import { TableCell } from "../Measures-table/TableCell";
import { ColumnSettingsPopover, ColumnSettingsPopoverProps } from "./ColumnSettingsPopover";
import { ColumnSettings, tableProps, titleColumn } from "./ReportMeasuresTable";
import { ReportTableToolbar } from "./ReportTableToolbar";
import { TableHeader } from "./TableHeader";
import { TableToolbar } from "./TableToolbar";

const SYSTEM_COLUMNS = ["mrt-row-expand", "pme_warning", "title"];

const extractKey = (id: string) => id.replace(/-(\d+)$/, "");
const isSystemColumn = (key: string) => SYSTEM_COLUMNS.includes(key);
const buildColumnId = (key: string, index: number) => `${key}-${index}`;
const makeColumnOrder = (columns: ReportTableColumn[], tableColumns: { key: string }[]) => [
  ...SYSTEM_COLUMNS,
  ...columns
    .filter((col): col is ReportTableColumn & { key: string } => Boolean(col.key))
    .map((col) => {
      const idx = tableColumns.findIndex((c) => c.key === col.key);
      return buildColumnId(col.key, idx);
    })
];

type Props = {
  isLoading: boolean;
  groupByOptions: SelectOption[];
  setMarketPlaceDealId: React.Dispatch<React.SetStateAction<number | null>>;
};

export const useReportsTable = ({ isLoading, groupByOptions, setMarketPlaceDealId }: Props) => {
  const measuresTable = useReportsTableSelector();
  const { hedge_fund, adjustable_columns } = useAccountSelector() || {};
  const numberFormat = useNumberFormatSelector();
  const {
    originalCurrency,
    rowBy,
    groupBy,
    columnBy,
    measures,
    characteristics,
    calculatedFields,
    reportTableColumns,
    indexName,
    secondaryPricing,
    dateRange: defaultDateRange,
    currency: defaultCurrency,
    use_accounting_date: defaultUseAccountingDate
  } = useReportsQueriesSelector();

  const { setQueriesAction, setIsHedgeFundsAction } = useReportsActions();
  const { updateSnapshotTableColumnsAction } = useUserActions();

  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [columnSettings, setColumnSettings] = useState<ColumnSettings>({ open: false, isDuplicate: false });

  const handleColumnOrderChange = (updater: React.SetStateAction<MRT_ColumnOrderState>) => {
    const newOrder = typeof updater === "function" ? updater(columnOrder) : updater;

    // Always keep the local order in sync so the drag is reflected visually in every view.
    setColumnOrder(newOrder);

    // Persisting the order only makes sense in the measure view, where table columns map
    // 1:1 to reportTableColumns. In timeline views (month/quarter/year/timeline) the columns
    // are time periods that don't exist in reportTableColumns, so the reorder stays ephemeral.
    if (columnBy !== "measure" || !reportTableColumns?.length) return;
    // if (!reportTableColumns?.length) return;

    const reordered = newOrder
      .map((id) => {
        const key = extractKey(id);
        if (isSystemColumn(key)) return null;
        return reportTableColumns.find((col) => col.key === key);
      })
      .filter(Boolean) as ReportTableColumn[];

    const isSameAsStore =
      reordered.length === reportTableColumns.length &&
      reordered.every((col, i) => col.key === reportTableColumns[i]?.key);

    if (!isSameAsStore && reordered.length > 0) {
      setQueriesAction({
        reportTableColumns: reordered
      });
      updateSnapshotTableColumnsAction(reordered);
    }
  };

  useEffect(
    () => () => {
      setColumnOrder([]);
    },
    []
  );

  const getNextOrder = () => {
    if (!measuresTable?.columns) return [];

    // Measure view: order derived from the persisted reportTableColumns (and persisted on drag).
    // Timeline views (month/quarter/year/timeline): columns are time periods that don't exist in
    // reportTableColumns, so we use their natural order. MRT still needs a full, valid columnOrder
    // here — an empty one breaks MRT's reorderColumn (splice on indexOf === -1), so the drag wouldn't
    // work even visually.
    if (columnBy === "measure") {
      if (!reportTableColumns?.length) return [];
      return makeColumnOrder(reportTableColumns, measuresTable.columns);
    }

    return [...SYSTEM_COLUMNS, ...measuresTable.columns.map((col, index) => buildColumnId(col.key, index))];
  };

  // Signature of the actual table columns. Using only `columns.length` as a dependency misses
  // switches between column sets of the same size (e.g. swapping columnBy modes), which leaves a
  // stale columnOrder whose ids no longer match the columns and breaks MRT's reorder.
  const columnsSignature = measuresTable?.columns?.map((c) => c.key).join("|") ?? "";

  useEffect(() => {
    const nextOrder = getNextOrder();
    if (!nextOrder.length) return;

    setColumnOrder((prev) => {
      const isSame = prev.length === nextOrder.length && prev.every((v, i) => v === nextOrder[i]);

      if (isSame) return prev;
      return nextOrder;
    });
  }, [columnsSignature, reportTableColumns, columnBy]);

  const columnRefs = useRef(new Map());

  const getColumnRef = (colId: string) => {
    if (!columnRefs.current.has(colId)) {
      columnRefs.current.set(colId, React.createRef<HTMLElement>());
    }
    return columnRefs.current.get(colId);
  };

  const handleColumnSettings: ColumnSettingsPopoverProps["onApply"] = (queries) => {
    const { abbreviated, decimal, dateRange, currency, use_accounting_date } = queries || {};
    if (columnSettings.column) {
      const { column, isDuplicate } = columnSettings;
      const index = reportTableColumns.findIndex((col) => col.key === column.key);

      if (isDuplicate) {
        // Parent column keeps its original settings or defaults
        const parentColumn: ReportTableColumn = {
          ...column,
          dateRange: column.dateRange || defaultDateRange,
          currency: column.currency || defaultCurrency,
          use_accounting_date: column.use_accounting_date ?? defaultUseAccountingDate,
          abbreviated: column.abbreviated || numberFormat.abbreviated,
          decimal: column.decimal ?? numberFormat.decimal
        };

        // Create duplicate column with new settings
        const duplicateColumn: ReportTableColumn = {
          ...column,
          key: column.key + "-dup",
          dateRange: dateRange,
          currency: currency,
          use_accounting_date,
          abbreviated: abbreviated || numberFormat.abbreviated,
          decimal: decimal ?? numberFormat.decimal
        };

        const updatedColumns = [
          ...reportTableColumns.slice(0, index),
          parentColumn,
          duplicateColumn,
          ...reportTableColumns.slice(index + 1)
        ];
        setQueriesAction({
          reportTableColumns: updatedColumns
        });
        updateSnapshotTableColumnsAction(updatedColumns);
      } else {
        // Just update the column
        const newColumn: ReportTableColumn = {
          ...column,
          dateRange: dateRange,
          currency: currency,
          abbreviated: abbreviated || numberFormat.abbreviated,
          decimal: decimal ?? numberFormat.decimal,
          use_accounting_date
        };

        const updatedColumns = [
          ...reportTableColumns.slice(0, index),
          newColumn,
          ...reportTableColumns.slice(index + 1)
        ];
        setQueriesAction({
          reportTableColumns: updatedColumns
        });
        updateSnapshotTableColumnsAction(updatedColumns);
      }
    }
    setColumnSettings({ open: false, isDuplicate: false });
  };

  const handleDelete = (column: ReportTableColumn) => {
    const index = reportTableColumns.findIndex((col) => col.key === column?.key);
    if (index > -1) {
      const updatedColumns = [...reportTableColumns.slice(0, index), ...reportTableColumns.slice(index + 1)];
      setQueriesAction({
        reportTableColumns: updatedColumns
      });
      updateSnapshotTableColumnsAction(updatedColumns);
    }
  };

  const measureTableColumns = useMemo(() => {
    if (!measuresTable) {
      return [];
    }
    const columns: MRT_ColumnDef<ClientDealTableRow>[] = measuresTable.columns.map<MRT_ColumnDef<ClientDealTableRow>>(
      (col, index) => {
        const colId = col.key + "-" + index;
        const columnData = reportTableColumns.find((column) => column.key === col.key) || {
          key: "",
          aggregation: ""
        };
        const isSecondaryPricing = col.key === "Secondary Pricing";

        return {
          header: "",
          Header: ({ column, table }) => (
            <TableHeader
              column={column}
              table={table}
              onClick={(e) => {
                e.stopPropagation();
                if (columnBy === "measure") {
                  setColumnSettings({
                    open: true,
                    column: {
                      ...columnData,
                      abbreviated: columnData.abbreviated || numberFormat.abbreviated,
                      decimal: columnData.decimal ?? numberFormat.decimal
                    },
                    isDuplicate: false,
                    anchorEl: getColumnRef(colId).current
                  });
                }
              }}
            />
          ),
          id: colId,
          meta: { data: col, def: columnData },
          accessorKey: col.key,
          enableSorting: !isSecondaryPricing,
          enableColumnActions: !isSecondaryPricing && adjustable_columns && columnBy === "measure",
          sortingFn: (rowA, rowB, columnId) => {
            const isTotalA = rowA.original.title === "Total";
            const isTotalB = rowB.original.title === "Total";
            if (isTotalA || isTotalB) {
              if (isTotalA && isTotalB) return 0;
              // MRT multiplies result by -1 for desc, so we counteract it to always pin Total first
              const isDesc =
                rowA
                  .getAllCells()
                  .find((c) => c.column.id === columnId)
                  ?.column.getIsSorted() === "desc";
              return isTotalA ? (isDesc ? 1 : -1) : isDesc ? -1 : 1;
            }
            const a = rowA.getValue<number>(columnId) ?? 0;
            const b = rowB.getValue<number>(columnId) ?? 0;
            return a - b;
          },
          accessorFn: (row) => row.valueData[col.key!],
          muiTableHeadCellProps: {
            ...tableProps.defaultColumn?.muiTableHeadCellProps,
            ref: getColumnRef(colId),
            sx: {
              " .Mui-TableHeadCell-Content": { flexGrow: 1 },
              " .MuiDivider-root": { borderWidth: 1, opacity: 0 },
              ...(isSecondaryPricing && { minWidth: "250px !important" })
            }
          },

          muiTableBodyCellProps: {
            sx: { ...(isSecondaryPricing && { minWidth: "250px !important" }) }
          },
          Cell: ({ row, column }) => {
            const cellValue = row.original.valueData[column.columnDef.accessorKey as string];

            if (isSecondaryPricing) {
              const placedOnMarket = row.original?.placed_on_market;
              // Format valueData for secondary pricing display
              const formattedValue = cellValue === null || cellValue === undefined ? "-" : String(cellValue);
              return (
                <PricingCell
                  value={formattedValue}
                  secondary_pricing_title={row.original.secondary_pricing_title}
                  placedOnMarket={placedOnMarket}
                  dealId={row.original.deal_id}
                  onPlaceOnMarket={setMarketPlaceDealId}
                />
              );
            }

            //if overrides exist characteristics for different investors show tooltip and ! icon
            const overridesTooltip = row.original.valueData[col.key + "_Tooltip"]
              ? String(row.original.valueData[col.key + "_Tooltip"])
              : "";

            return (
              <TableCell
                id={column.id}
                value={cellValue}
                postfix={
                  row.original.postfixes?.[column.columnDef.accessorKey as string] ||
                  col.postfix ||
                  row.original.postfix
                }
                abbreviated={columnData?.abbreviated || numberFormat.abbreviated}
                decimal={columnData?.decimal ?? numberFormat.decimal}
                overridesTooltip={overridesTooltip}
              />
            );
          },
          AggregatedCell: ({ row, column }) => {
            const cellValue = row.original.valueData[column.columnDef.accessorKey as string];

            //if overrides exist characteristics for different investors show tooltip and ! icon
            const overridesTooltip = row.original.valueData[col.key + "_Tooltip"]
              ? String(row.original.valueData[col.key + "_Tooltip"])
              : "";

            return (
              <TableCell
                id={column.id}
                value={cellValue}
                postfix={
                  row.original.postfixes?.[column.columnDef.accessorKey as string] ||
                  col.postfix ||
                  row.original.postfix
                }
                overridesTooltip={overridesTooltip}
                aggregated
                abbreviated={columnData?.abbreviated || numberFormat.abbreviated}
                decimal={columnData?.decimal ?? numberFormat.decimal}
              />
            );
          }
        };
      }
    );

    columns.unshift(titleColumn);
    columns.unshift({
      header: "",
      id: "pme_warning",
      accessorKey: "pme_warning",
      enableColumnDragging: false,
      enableColumnFilter: false,
      enableColumnActions: false,
      enableResizing: false,
      enableSorting: false,
      enableColumnOrdering: false,
      size: 20,
      muiTableHeadCellProps: {
        sx: {
          padding: "0",
          minWidth: "10px",
          width: "15px"
        }
      },
      muiTableBodyCellProps: {
        sx: {
          padding: "0",
          minWidth: "10px",
          width: "15px"
        }
      },
      Cell: ({ row }) =>
        Boolean(row.original.pme_warning) && (
          <MaterialTooltip isShow title={row.original.pme_warning} centerText placement="top">
            <InfoOutlinedIcon fontSize="sm" sx={{ fill: "#071F35" }} />
          </MaterialTooltip>
        )
    } as MRT_ColumnDef<ClientDealTableRow>);
    return columns;
  }, [measuresTable, reportTableColumns, numberFormat, columnBy]);

  const table = useMaterialReactTable<ClientDealTableRow>({
    // Common table Props
    ...tableProps,
    getRowId: (row) => row.id!,
    getSubRows: (row) => row.children,
    filterFromLeafRows: true,
    enableColumnActions: true,
    enableBottomToolbar: false,
    layoutMode: "grid",
    // Specific table Props
    columns: measureTableColumns,
    enableColumnResizing: true,

    data: measuresTable?.rows || [],
    enableColumnOrdering: true,
    onColumnOrderChange: handleColumnOrderChange,

    localization: {
      sortByColumnAsc: "Sort",
      sortByColumnDesc: "Sort",
      sortedByColumnAsc: "Increase",
      sortedByColumnDesc: "Decrease",
      clearSort: "Clear Sort"
    },

    // Add styling for the expand/collapse column
    // Width must accommodate: (max_depth * 16px margin) + ~36px button
    // With 5 groups + rowBy, max depth is ~5, requiring 80px + 36px = 116px minimum
    muiTableBodyCellProps: ({ column }) => ({
      sx:
        column.id === "mrt-row-expand"
          ? {
              width: "120px",
              maxWidth: "120px",
              minWidth: "120px",
              padding: "0px 5px"
            }
          : {}
    }),
    muiTableHeadRowProps: { sx: { borderRadius: "8px 8px 0 0" } },
    muiTableHeadCellProps: ({ column }) => ({
      sx:
        column.id === "mrt-row-expand"
          ? {
              width: "120px",
              maxWidth: "120px",
              minWidth: "120px",
              padding: "0px 5px",
              borderRadius: "8px 8px 0 0"
            }
          : {}
    }),

    defaultColumn: {
      ...tableProps.defaultColumn,
      muiColumnDragHandleProps: { sx: { position: "absolute", left: -22, top: 0, opacity: 0 } },
      enableColumnActions: adjustable_columns,
      renderColumnActionsMenuItems: ({ column, closeMenu, table }) => {
        const otherColumns = table.getAllColumns().filter((col) => col !== column);
        const deletable = otherColumns.some(
          //@ts-ignore
          (col) => col.columnDef.meta?.def?.aggregation === column.columnDef.meta?.def?.aggregation
        );
        const columnDef = reportTableColumns.find((col) => col.key === column.columnDef.accessorKey);

        // Only show duplicate and settings for measure columns
        if (columnBy !== "measure") {
          return [];
        }

        return [
          <MenuItem
            key="duplicate"
            onClick={() => {
              setColumnSettings({
                open: true,
                column: {
                  ...columnDef!,
                  abbreviated: columnDef?.abbreviated || numberFormat.abbreviated,
                  decimal: columnDef?.decimal ?? numberFormat.decimal
                },
                isDuplicate: true,
                anchorEl: getColumnRef(column.id).current
              });
              closeMenu();
            }}
          >
            <ListItemIcon>
              <ContentCopyIcon color="primary" />
            </ListItemIcon>
            <ListItemText>Duplicate Column</ListItemText>
          </MenuItem>,
          <MenuItem
            key="settings"
            onClick={() => {
              setColumnSettings({
                open: true,
                column: {
                  ...columnDef!,
                  abbreviated: columnDef?.abbreviated || numberFormat.abbreviated,
                  decimal: columnDef?.decimal ?? numberFormat.decimal
                },
                isDuplicate: false,
                anchorEl: getColumnRef(column.id).current
              });
              closeMenu();
            }}
          >
            <ListItemIcon>
              <TuneIcon color="primary" />
            </ListItemIcon>
            <ListItemText>Column Settings</ListItemText>
          </MenuItem>,
          ...(deletable
            ? [
                <MenuItem
                  key="delete"
                  onClick={() => {
                    handleDelete({
                      ...columnDef!,
                      abbreviated: columnDef?.abbreviated || numberFormat.abbreviated,
                      decimal: columnDef?.decimal ?? numberFormat.decimal
                    });
                    closeMenu();
                  }}
                >
                  <ListItemIcon>
                    <DeleteIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </MenuItem>
              ]
            : [])
        ];
      }
    },

    enableExpanding: true,
    state: { isLoading: !measuresTable || isLoading, columnOrder: columnOrder },
    renderTopToolbar: ({ table }) => {
      return (
        <TableToolbar
          setFullScreen={() => table.setIsFullScreen(!table.getState().isFullScreen)}
          isFullScreen={table.getState().isFullScreen}
          {...(hedge_fund && { tabs: { value: "general", onChange: () => setIsHedgeFundsAction(true) } })}
        >
          <ReportTableToolbar
            groupByOptions={groupByOptions}
            measures={measures}
            characteristics={characteristics}
            calculatedFields={calculatedFields}
            secondaryPricing={secondaryPricing}
            groupBy={groupBy}
            columnBy={columnBy}
            rowBy={rowBy}
            originalCurrency={originalCurrency}
            indexName={indexName}
            isTableLoading={isLoading}
            isSnapshot={true}
          />
        </TableToolbar>
      );
    }
  });

  const popover = (
    <ColumnSettingsPopover
      {...columnSettings}
      onClose={() => setColumnSettings({ open: false, isDuplicate: false })}
      onApply={handleColumnSettings}
    />
  );

  return {
    table,
    popover
  };
};
